import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ApiService } from '../services/api';
import { PipelineResult, SchemePageInfo } from '../types/models';
import { useApplicationPolling } from '../hooks/useApplicationPolling';
import { 
  DocumentTextIcon, 
  RectangleStackIcon, 
  ArrowLeftIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

// Вспомогательная функция для проверки завершения разметки (все устройства имеют chunk_gcs_uri)
const isMarkupComplete = (schemeData: SchemePageInfo): boolean => {
  if (!schemeData?.parts?.devices || schemeData.parts.devices.length === 0) {
    return false;
  }
  return schemeData.parts.devices.every(device => 
    device.chunk_gcs_uri !== null && device.chunk_gcs_uri !== undefined && device.chunk_gcs_uri !== ''
  );
};

// Вспомогательная функция для определения статуса разметки
const getMarkupStatus = (schemeData: SchemePageInfo): 'required' | 'complete' | 'not_needed' => {
  const deviceCount = schemeData?.parts?.devices?.length ?? 0;
  
  if (deviceCount === 1) {
    return 'not_needed';
  }
  
  if (deviceCount > 1) {
    return isMarkupComplete(schemeData) ? 'complete' : 'required';
  }
  
  return 'not_needed';
};

// Вспомогательная функция для проверки наличия требуемой разметки
const hasRequiredMarkup = (pdResult: PipelineResult): boolean => {
  return pdResult.scheme_pages.some(scheme => getMarkupStatus(scheme) === 'required');
};

// Вспомогательная функция для проверки завершения подбора комплектующих (все specs имеют spn_id)
const isProductSelectionComplete = (pdResult: PipelineResult): boolean => {
  if (!pdResult.scheme_pages || pdResult.scheme_pages.length === 0) {
    return false;
  }
  
  for (const scheme of pdResult.scheme_pages) {
    if (!scheme.parts?.devices || scheme.parts.devices.length === 0) {
      continue; // Пропускаем схемы без устройств
    }
    
    for (const device of scheme.parts.devices) {
      if (!device.specs || device.specs.length === 0) {
        continue; // Пропускаем устройства без specs
      }
      
      // Если хотя бы один spec не имеет spn_id, подбор не завершен
      for (const spec of device.specs) {
        if (!spec.spn_id) {
          return false;
        }
      }
    }
  }
  
  return true;
};

const PDResultPage: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const [pdResult, setpdResult] = useState<PipelineResult | null>(null);
  const [reportContent, setReportContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRunningSelection, setIsRunningSelection] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [selectionSuccess, setSelectionSuccess] = useState(false);
  const [optimisticProcessingStatus, setOptimisticProcessingStatus] = useState<string | null>(null);

  // Polling статуса заявки
  const { status } = useApplicationPolling(
    appId ? Number.parseInt(appId) : null,
    10000,  // 10 секунд
    true
  );

  const effectiveProcessingStatus = optimisticProcessingStatus || status?.processing_status;

  useEffect(() => {
    if (appId) {
      loadPDResult(parseInt(appId));
    }
  }, [appId]);
  
  // Обновляем pdResult при изменении status из polling
  useEffect(() => {
    if (status?.pd_result) {
      setpdResult(status.pd_result);
    }
  }, [status]);

  useEffect(() => {
    if (status?.processing_status === 'selecting_products') {
      setOptimisticProcessingStatus(null);
    }
  }, [status?.processing_status]);

  useEffect(() => {
    if (pdResult && isProductSelectionComplete(pdResult)) {
      setOptimisticProcessingStatus(null);
    }
  }, [pdResult]);

  const loadPDResult = async (applicationId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getPDResult(applicationId);
      setpdResult(response.pd_result);
      setReportContent(response.report_content);
    } catch (err) {
      console.error('Error loading PD result:', err);
      setError('Ошибка при загрузке результатов обработки');
    } finally {
      setLoading(false);
    }
  };

  const handleRunProductSelection = async () => {
    if (!appId) return;
    
    setSelectionError(null);
    setSelectionSuccess(false);
    setIsRunningSelection(true);
    setOptimisticProcessingStatus('selecting_products');

    try {
      // Get current user ID (you'll need to adjust this based on your auth implementation)
      const userResponse = await ApiService.checkAuthStatus();
      const userId = userResponse.user?.id;
      
      if (!userId) {
        throw new Error('Не удалось определить ID пользователя');
      }

      const response = await ApiService.runProductSelection(parseInt(appId), userId);
      
      if (response.success) {
        setSelectionSuccess(true);
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Немедленно перезагружаем статус заявки для обновления processing_status
        setTimeout(async () => {
          try {
            await loadPDResult(parseInt(appId));
          } catch (error) {
            console.error('Error reloading PD result:', error);
          }
        }, 1000); // Даем бэкенду 1 секунду на обновление статуса
      } else {
        setSelectionError(response.message);
        setOptimisticProcessingStatus(null);
      }
    } catch (err) {
      console.error('Error running product selection:', err);
      setSelectionError(err instanceof Error ? err.message : 'Неизвестная ошибка при запуске подбора');
      setOptimisticProcessingStatus(null);
    } finally {
      setIsRunningSelection(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <SparklesIcon className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Загрузка результатов...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Вернуться назад
          </button>
        </div>
      </div>
    );
  }

  // Если нет pd_result, показываем текстовый отчет (legacy)
  if (!pdResult && reportContent) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Назад
          </button>
          <div className="bg-white rounded-lg shadow p-6">
            <h1 className="text-2xl font-bold mb-4 flex items-center">
              <DocumentTextIcon className="h-6 w-6 mr-2" />
              Отчет по проектной документации
            </h1>
            <div className="prose max-w-none whitespace-pre-wrap">
              {reportContent}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!pdResult) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <p className="text-gray-600">Результаты обработки не найдены</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Назад
          </button>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <DocumentTextIcon className="h-8 w-8 mr-3" />
            Результаты обработки проектной документации
          </h1>
          <p className="text-gray-600 mt-2">
            Всего страниц: {pdResult.total_pages} | Всего устройств: {pdResult.total_devices}
          </p>
        </div>

        {/* Error Message */}
        {selectionError && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-700">{selectionError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {selectionSuccess && (
          <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-700">
                  Подбор оборудования успешно запущен. Результаты будут загружены на страницу заявки.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Scheme Pages Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-2 flex items-center text-gray-900">
            <RectangleStackIcon className="h-6 w-6 mr-2" />
            Страницы со схемами
          </h2>
          
          {/* Warning about markup required */}
          {hasRequiredMarkup(pdResult) && (
            <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 rounded">
              <p className="text-red-700 font-medium text-sm">
                ⚠️ Необходимо произвести разметку всех схем, отмеченных как "Требуется разметка схемы"
              </p>
              <p className="text-red-600 text-xs mt-1">
                Разметка схемы требуется для точного определения состава комплектующих на каждой странице документации. После разметки вам будет доступна возможность запустить подбор комплектующих и расчет стоимости проекта.
              </p>
            </div>
          )}
          
          {/* Recommendation to review before starting selection */}
          {!hasRequiredMarkup(pdResult) && !isProductSelectionComplete(pdResult) && effectiveProcessingStatus !== 'selecting_products' && (
            <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
              <p className="text-blue-700 font-medium text-sm">
                💡 Рекомендуется проверить распознанные данные перед запуском подбора оборудования
              </p>
              <p className="text-blue-600 text-xs mt-1">
                Просмотрите каждую схему, убедитесь в правильности определения устройств и их количества. Это поможет повысить точность автоматического подбора комплектующих.
              </p>
            </div>
          )}
          
          {/* Warning about product selection in progress */}
          {effectiveProcessingStatus === 'selecting_products' && (
            <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
              <p className="text-blue-700 font-medium text-sm">
                ⏳ Идет подбор комплектующих. Редактирование схем временно недоступно.
              </p>
            </div>
          )}
          
          {pdResult.scheme_pages.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pdResult.scheme_pages.map((schemePage, index) => {
                const status_markup = getMarkupStatus(schemePage);
                const isSelectingProducts = effectiveProcessingStatus === 'selecting_products';
                
                return (
                  <Link
                    key={index}
                    to={isSelectingProducts ? '#' : `/applications/${appId}/schemes/${index}`}
                    className={`block p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-2 
                      ${isSelectingProducts 
                        ? 'border-gray-300 cursor-not-allowed opacity-50' 
                        : 'border-blue-200 hover:border-blue-400 hover:shadow-lg transition transform hover:-translate-y-1'
                      }`}
                    onClick={(e) => {
                      if (isSelectingProducts) {
                        e.preventDefault();
                        alert('Дождитесь завершения подбора комплектующих');
                      }
                    }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-sm font-semibold text-blue-600">
                        Страница {schemePage.page}
                      </span>
                      {schemePage.qty && (
                        <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                          ×{schemePage.qty}
                        </span>
                      )}
                    </div>
                    
                    {schemePage.device_name && (
                      <p className="text-sm font-medium text-gray-800 mb-2">
                        {schemePage.device_name}
                      </p>
                    )}
                    
                    {schemePage.parts && schemePage.parts.devices && (
                      <p className="text-xs text-gray-600 mb-2">
                        Устройств на схеме: {schemePage.parts.devices.length}
                      </p>
                    )}
                    
                    {/* Processing Status Badge (yellow) */}
                    {schemePage.processing_status === 'processing' && (
                      <div className="mb-3 flex items-center text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full border border-yellow-200">
                        <SparklesIcon className="h-4 w-4 mr-1 animate-spin" />
                        <span className="font-medium">Обрабатывается</span>
                      </div>
                    )}
                    
                    {/* Markup Status Badge */}
                    {schemePage.processing_status !== 'processing' && status_markup === 'required' && (
                      <div className="mb-3 flex items-center text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full border border-red-200">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        <span className="font-medium">Требуется разметка схемы</span>
                      </div>
                    )}
                    
                    {schemePage.processing_status !== 'processing' && status_markup === 'complete' && (
                      <div className="mb-3 flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-200">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        <span className="font-medium">Разметка произведена</span>
                      </div>
                    )}
                    
                    {schemePage.processing_status !== 'processing' && status_markup === 'not_needed' && (
                      <div className="mb-3 flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-200">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        <span className="font-medium">Разметка не требуется</span>
                      </div>
                    )}
                    
                    <div className="mt-3 text-sm text-blue-600 font-medium flex items-center">
                      Просмотреть схему
                      <svg
                        className="w-4 h-4 ml-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 italic">Страницы со схемами не найдены</p>
          )}
        </div>

        {/* Devices List Section - ВРЕМЕННО СКРЫТА */}
        {/* <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-900">
            <CubeIcon className="h-6 w-6 mr-2" />
            Состав устройств в разделе ПД
          </h2>
          {pdResult.devices_list.length > 0 ? (
            <div className="space-y-2">
              {pdResult.devices_list.map((device, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center p-3 bg-gray-50 rounded hover:bg-gray-100 transition"
                >
                  <span className="font-medium text-gray-800">{device.device}</span>
                  <span className="text-sm text-gray-600 bg-blue-100 px-3 py-1 rounded-full">
                    Количество: {device.qty}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">Устройства не найдены</p>
          )
          }
        </div> */}

        {/* Run Product Selection Button */}
        <style>{`
          .button-group:hover .tooltip {
            opacity: 1;
            visibility: visible;
            transition: opacity 0.1s ease-in, visibility 0.1s ease-in;
          }
          .tooltip {
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.3s ease-out, visibility 0.3s ease-out;
          }
        `}</style>
        <div className="flex justify-center button-group relative">
          {isProductSelectionComplete(pdResult) ? (
            // Если подбор завершен - показываем кнопку перехода
            <button
              onClick={() => navigate(`/applications/${appId}`)}
              className="px-8 py-3 rounded-lg font-semibold flex items-center transition bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800"
            >
              <RectangleStackIcon className="h-5 w-5 mr-2" />
              Перейти на панель подбора
            </button>
          ) : (
            // Если подбор не завершен - показываем кнопку запуска
            <>
              {/* Определяем статусы для блокировки */}
              {(() => {
                const isProcessingSchemes = effectiveProcessingStatus === 'processing_schemes';
                const isSelectingProducts = effectiveProcessingStatus === 'selecting_products';
                const hasUnprocessedSchemes = hasRequiredMarkup(pdResult);
                const isBlocked = isRunningSelection || isProcessingSchemes || isSelectingProducts || hasUnprocessedSchemes;

                return (
                  <button
                    onClick={handleRunProductSelection}
                    disabled={isBlocked}
                    className={`px-8 py-3 rounded-lg font-semibold flex items-center transition ${
                      isBlocked
                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                    }`}
                  >
                    {isSelectingProducts ? (
                      <>
                        <SparklesIcon className="h-5 w-5 mr-2 animate-spin" />
                        Подбор оборудования запущен...
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="h-5 w-5 mr-2" />
                        Запустить подбор оборудования
                      </>
                    )}
                  </button>
                );
              })()}
            </>
          )}
          
          {/* Tooltip for disabled state */}
          {(() => {
            const isProcessingSchemes = effectiveProcessingStatus === 'processing_schemes';
            const isSelectingProducts = effectiveProcessingStatus === 'selecting_products';
            const hasUnprocessedSchemes = hasRequiredMarkup(pdResult);

            if (isProcessingSchemes) {
              return (
                <div className="absolute bottom-full mb-2 tooltip bg-gray-900 text-white text-sm rounded px-3 py-2 whitespace-nowrap z-10">
                  Дождитесь завершения обработки схем
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              );
            }

            if (isSelectingProducts) {
              return (
                <div className="absolute bottom-full mb-2 tooltip bg-gray-900 text-white text-sm rounded px-3 py-2 whitespace-nowrap z-10">
                  Идет подбор комплектующих. Дождитесь завершения...
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              );
            }

            if (hasUnprocessedSchemes && !isProductSelectionComplete(pdResult)) {
              return (
                <div className="absolute bottom-full mb-2 tooltip bg-gray-900 text-white text-sm rounded px-3 py-2 whitespace-nowrap z-10">
                  Необходимо произвести разметку всех схем, отмеченных как "Требуется разметка схемы"
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              );
            }

            return null;
          })()}
        </div>
      </div>
    </div>
  );
};

export default PDResultPage;
